/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Billing.tsx
 * DESCRIPTION: Billing and subscription management page with Stripe
 *              integration. Allows users to view current plan, upgrade,
 *              downgrade, and manage payment methods.
 *              Updated with dark mode support and improved UI/UX.
 *
 * FEATURES:
 * - Current plan stats in dashboard-style cards
 * - Tabbed interface for Plans and Invoices
 * - Improved pricing plan cards
 * - Dark mode support throughout
 * - Fully functional with mock data
 * - Ready for live API integration
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.1
 * @created 2025-01-15
 * @updated 2026-03-27 - Redesigned with dark mode, tabbed interface,
 *                       dashboard-style cards, improved UX
 * @updated 2026-04-03 - Removed unused imports, fixed Stripe redirect
 *                       method for compatibility with latest Stripe.js
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser } from '@clerk/react';
import {
  Check,
  CreditCard,
  Download,
  AlertCircle,
  HardDrive,
  FileText,
  Zap,
  Star,
  Clock,
} from 'lucide-react';
import {
  stripeApi,
  SUBSCRIPTION_PLANS,
  SubscriptionPlan,
  SubscriptionStatus,
  initializeStripe,
} from '../lib/stripeApi';

/**
 * Billing Component
 * Manages subscription plans, payment methods, and billing history.
 *
 * @returns {JSX.Element} Billing and subscription management interface
 */
const Billing: React.FC = () => {
  // Removed unused 'user' destructuring - useUser() still needed for auth state
  const { isLoaded } = useUser();
  const [selectedPlan, setSelectedPlan] = useState<string>('pro');
  const [activeTab, setActiveTab] = useState<'plans' | 'invoices'>('plans');
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Load subscription and invoice data on mount
   */
  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      const [subData, invoiceData] = await Promise.all([
        stripeApi.getSubscriptionStatus(),
        stripeApi.getInvoices(),
      ]);
      setSubscription(subData);
      setInvoices(invoiceData);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle subscription upgrade/checkout
   */
  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (plan.price === 0) {
      alert('Free plan selected - no payment required');
      return;
    }

    setIsProcessing(true);

    try {
      const stripe = await initializeStripe();
      if (!stripe) {
        alert('Stripe not configured. Please add your Stripe publishable key to .env');
        return;
      }

      const session = await stripeApi.createCheckoutSession(
        plan.id,
        `${window.location.origin}/billing?success=true`,
        `${window.location.origin}/billing?canceled=true`
      );

      // Fixed: Use session.url directly instead of deprecated redirectToCheckout
      if (session.url) {
        window.location.href = session.url;
      } else if (session.sessionId) {
        // Fallback: Redirect to Stripe Checkout URL
        window.location.href = `https://checkout.stripe.com/c/pay/${session.sessionId}`;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Open Stripe Customer Portal
   */
  const handleManageSubscription = async () => {
    try {
      const session = await stripeApi.createPortalSession(
        `${window.location.origin}/billing`
      );
      window.location.href = session.url;
    } catch (error: any) {
      console.error('Portal error:', error);
      alert('Failed to open billing portal');
    }
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  if (isLoading || !isLoaded) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Billing & Subscription
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your subscription, payment methods, and billing history
          </p>
        </div>

        {/* Success/Canceled Messages */}
        {window.location.search.includes('success=true') && (
          <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-3">
            <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-emerald-800 dark:text-emerald-300">
              Payment successful! Your subscription is now active.
            </span>
          </div>
        )}

        {window.location.search.includes('canceled=true') && (
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <span className="text-yellow-800 dark:text-yellow-300">
              Checkout was canceled. No charges were made.
            </span>
          </div>
        )}

        {/* Current Plan Stats - Dashboard Style Cards */}
        {subscription && (
          <div className="mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Current Plan
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {subscription.currentPlan?.name || 'Free'} -{' '}
                    <span className="capitalize">{subscription.status}</span>
                  </p>
                </div>
                <button
                  onClick={handleManageSubscription}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                >
                  <CreditCard className="w-5 h-5" />
                  Manage Subscription
                </button>
              </div>

              {/* Usage Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Files Analyzed</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        185 <span className="text-sm font-normal text-gray-500">/ 1,000</span>
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: '18.5%' }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">API Usage</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        73,450 <span className="text-sm font-normal text-gray-500">/ 100,000</span>
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full transition-all"
                      style={{ width: '73.45%' }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <HardDrive className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Storage Used</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        28.5 GB <span className="text-sm font-normal text-gray-500">/ 100 GB</span>
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-emerald-600 h-2 rounded-full transition-all"
                      style={{ width: '28.5%' }}
                    />
                  </div>
                </div>
              </div>

              {/* Renewal Date */}
              {subscription.currentPeriodEnd && (
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <p className="text-sm text-blue-900 dark:text-blue-300">
                    Next billing date:{' '}
                    <span className="font-medium">
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pricing Plans & Invoices - Tabbed Interface */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav className="flex gap-6">
              <button
                onClick={() => setActiveTab('plans')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'plans'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Star className="w-4 h-4" />
                Subscription Plans
              </button>
              <button
                onClick={() => setActiveTab('invoices')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'invoices'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Download className="w-4 h-4" />
                Invoices
              </button>
            </nav>
          </div>

          {/* Plans Tab */}
          {activeTab === 'plans' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Choose Your Plan
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative rounded-xl border-2 p-6 transition-all cursor-pointer ${
                      subscription?.currentPlan?.id === plan.id
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : selectedPlan === plan.id
                        ? 'border-blue-600 bg-white dark:bg-gray-800'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                          Most Popular
                        </span>
                      </div>
                    )}
                    {subscription?.currentPlan?.id === plan.id && (
                      <div className="absolute -top-3 right-3">
                        <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-3 py-1 rounded-full text-xs font-medium">
                          Current Plan
                        </span>
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {plan.name}
                    </h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900 dark:text-white">
                        ${plan.price}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">/{plan.period}</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                      {plan.description}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {subscription?.currentPlan?.id === plan.id ? (
                      <button
                        className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium cursor-not-allowed"
                        disabled
                      >
                        Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(plan)}
                        disabled={isProcessing}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : plan.price === 0 ? 'Get Started' : 'Subscribe Now'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Billing History
              </h2>
              <div className="space-y-3">
                {invoices.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No invoices yet</p>
                  </div>
                ) : (
                  invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                          <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{invoice.id}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{invoice.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(invoice.amount)}
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            invoice.status === 'paid'
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                          }`}
                        >
                          {invoice.status}
                        </span>
                        <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {invoices.length > 0 && (
                <button className="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mt-6">
                  View All Invoices
                </button>
              )}
            </div>
          )}
        </div>

        {/* Payment Method */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Payment Method
          </h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <CreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">•••• •••• •••• 4242</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Expires 12/25</p>
              </div>
              <button
                onClick={handleManageSubscription}
                className="px-4 py-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
              >
                Update
              </button>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Billing Address</p>
              <p className="font-medium text-gray-900 dark:text-white">123 Business St</p>
              <p className="text-gray-700 dark:text-gray-300">San Francisco, CA 94105</p>
              <p className="text-gray-700 dark:text-gray-300">United States</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
